import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, UncontrolledTooltip } from 'reactstrap';
import {
	LayoutDashboard,
	Package,
	BadgePercent,
	FileText,
	MapPinned,
	Globe,
	LifeBuoy,
	Menu,
	X,
} from 'lucide-react';
import * as yup from 'yup';

import { fetchMyBusiness, editLogoImage } from '../../store/actions/business';
import headerImage from 'assets/img/image_placeholder.jpg';
import './BusinessSidebar.css';

const FILE_SIZE = 1e6;
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];

const schema = yup.object({
	files: yup
		.mixed()
		.nullable()
		.required('Image is required')
		.test(
			'fileSize',
			'File too large, select Images smaller than 1MB',
			(value) => value && value.size <= FILE_SIZE,
		)
		.test(
			'fileFormat',
			'Unsupported Format',
			(value) => value && SUPPORTED_FORMATS.includes(value.type),
		),
});

const menuItems = [
	{
		key: 'dashboard',
		label: 'Dashboard',
		to: '/dashboard',
		icon: LayoutDashboard,
		disabled: false,
	},
	{
		key: 'products',
		label: 'Products',
		to: '/products',
		icon: Package,
		disabled: false,
	},
	{
		key: 'deals',
		label: 'Deals',
		to: '/deals',
		icon: BadgePercent,
		disabled: false,
	},
	{
		key: 'marketing-posts',
		label: 'Marketing Posts',
		to: '/marketing',
		icon: FileText,
		disabled: false,
	},
	{
		key: 'market-with-locals',
		label: 'Add Building',
		to: '/building-search-page',
		icon: MapPinned,
		disabled: false,
	},
	{
		key: 'public-website',
		label: 'Public Website',
		to: '/admin',
		icon: Globe,
		disabled: false,
	},
	{
		key: 'support',
		label: 'Support',
		to: '/business-page/support',
		icon: LifeBuoy,
		disabled: true,
	},
];

const BusinessSidebar = () => {
	const dispatch = useDispatch();
	const location = useLocation();

	const business = useSelector((state) => state.myBusiness);
	const { id } = useSelector((state) => state.currentUser.user);

	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const [image, setImage] = useState(undefined);
	const [validIt, setValidIt] = useState(undefined);

	const inputFile = useRef(null);

	useEffect(() => {
		if (id) {
			dispatch(fetchMyBusiness(id));
		}
	}, [dispatch, id]);

	const handleClickImage = () => {
		dispatch(editLogoImage(id, image));
		setImage(undefined);
	};

	const handleClearInputFile = () => {
		if (inputFile.current) {
			inputFile.current.value = '';
		}
		setImage('');
	};

	const isActive = (to) => {
		return location.pathname === to;
	};

	return (
		<>
			<div className='business-sidebar-mobile-toggle d-md-none'>
				<Button
					className='business-sidebar-mobile-toggle__btn'
					onClick={() => setIsMobileOpen((prev) => !prev)}>
					{isMobileOpen ? <X size={18} /> : <Menu size={18} />}
					<span>Menu</span>
				</Button>
			</div>

			<aside
				className={`business-sidebar ${isMobileOpen ? 'business-sidebar--open' : ''}`}>
				<div className='business-sidebar__card'>
					<div className='business-sidebar__header'>
						<div className='business-sidebar__logo-wrap'>
							{!image && (
								<>
									<img
										className='business-sidebar__logo'
										src={business?.logo || headerImage}
										alt={business?.businessName || 'Business logo'}
									/>

									<Button
										className='business-sidebar__logo-edit'
										color='success'
										id='businessSidebarLogoEdit'
										size='sm'
										onClick={() => inputFile.current.click()}
										type='button'>
										<i className='nc-icon nc-settings-gear-65' />
									</Button>

									<UncontrolledTooltip
										delay={0}
										placement='right'
										target='businessSidebarLogoEdit'>
										Edit logo
									</UncontrolledTooltip>
								</>
							)}

							<input
								ref={inputFile}
								type='file'
								style={{ display: 'none' }}
								onChange={(e) => {
									setImage(e.target.files[0]);
									setValidIt(undefined);

									schema
										.validate({
											files: e.target.files[0],
										})
										.catch((err) => {
											setImage(undefined);
											setValidIt(err.errors);
										});
								}}
							/>

							{image && (
								<div className='business-sidebar__logo-preview'>
									<img
										className='business-sidebar__logo-preview-img'
										src={URL.createObjectURL(image)}
										alt='preview'
									/>
									<i
										onClick={handleClearInputFile}
										className='bi bi-x-circle'
									/>
									<i
										onClick={handleClickImage}
										className='bi bi-check-circle'
									/>
								</div>
							)}
						</div>

						<div className='business-sidebar__identity'>
							<h3>{business?.businessName || 'Your Business'}</h3>
							<p>{business?.businessSector || 'Business profile'}</p>
							{validIt && (
								<div className='business-sidebar__error'>{validIt}</div>
							)}
						</div>
					</div>

					<nav className='business-sidebar__nav'>
						{menuItems.map((item) => {
							const Icon = item.icon;

							if (item.disabled) {
								return (
									<div
										key={item.key}
										className='business-sidebar__link business-sidebar__link--disabled'>
										<Icon size={18} />
										<span>{item.label}</span>
									</div>
								);
							}

							return (
								<Link
									key={item.key}
									to={item.to}
									className={`business-sidebar__link ${
										isActive(item.to) ? 'business-sidebar__link--active' : ''
									}`}
									onClick={() => setIsMobileOpen(false)}>
									<Icon size={18} />
									<span>{item.label}</span>
								</Link>
							);
						})}
					</nav>

					<div className='business-sidebar__footer'>
						<p>Manage your storefront, local reach, and campaigns from here.</p>
					</div>
				</div>
			</aside>
		</>
	);
};

export default BusinessSidebar;
